import { refreshTokenStorage } from './refreshTokenStorage';
const AUTH_TOKEN_UPDATED_EVENT = 'icavi:auth-token-updated';
let inMemoryAccessToken = null;
const REFRESH_FAILURE_COOLDOWN_MS = 60 * 1000;
let refreshInFlightPromise = null;
let refreshBlockedUntil = 0;
const dispatchAuthTokenUpdate = () => {
    if (typeof window === 'undefined') {
        return;
    }
    try {
        const event = typeof CustomEvent === 'function'
            ? new CustomEvent(AUTH_TOKEN_UPDATED_EVENT)
            : new Event(AUTH_TOKEN_UPDATED_EVENT);
        window.dispatchEvent(event);
    }
    catch (error) {
        console.error('Erro ao despachar evento de atualizacao de token:', error);
    }
};
export const getSharedAccessToken = () => inMemoryAccessToken;
export const clearSharedAccessToken = () => {
    inMemoryAccessToken = null;
};
const normalizeMessage = (refreshResponse) => {
    const rawMessage = refreshResponse.erro ??
        refreshResponse.error ??
        refreshResponse.mensagem ??
        refreshResponse.message ??
        '';
    if (typeof rawMessage !== 'string') {
        return '';
    }
    return rawMessage
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase();
};
const isTemporaryRefreshFailure = (refreshResponse, normalizedMessage) => {
    const httpStatus = Number(refreshResponse.http_status ?? 0);
    if (httpStatus >= 500) {
        return true;
    }
    return (normalizedMessage.includes('temporariamente indisponivel') ||
        normalizedMessage.includes('tente novamente') ||
        normalizedMessage.includes('timeout') ||
        normalizedMessage.includes('temporarily unavailable'));
};
export const runSharedTokenRefresh = async ({ requestRefresh, }) => {
    if (refreshInFlightPromise) {
        return refreshInFlightPromise;
    }
    const executeRefresh = async () => {
        if (Date.now() < refreshBlockedUntil) {
            return {
                success: false,
                newToken: null,
                reason: 'cooldown_active',
            };
        }
        if (typeof window === 'undefined') {
            return {
                success: false,
                newToken: null,
                reason: 'non_browser_environment',
            };
        }
        const userData = localStorage.getItem('userData');
        if (!userData) {
            return {
                success: false,
                newToken: null,
                reason: 'missing_user_data',
            };
        }
        try {
            JSON.parse(userData);
        }
        catch (error) {
            console.error('Erro ao parsear userData no fluxo compartilhado de refresh:', error);
            return {
                success: false,
                newToken: null,
                reason: 'invalid_user_data',
            };
        }
        const currentRefreshToken = refreshTokenStorage.get();
        if (!currentRefreshToken) {
            return {
                success: false,
                newToken: null,
                reason: 'missing_refresh_token',
            };
        }
        let refreshResponse;
        try {
            refreshResponse = await requestRefresh(currentRefreshToken);
        }
        catch (error) {
            console.error('Erro ao renovar token:', error);
            return {
                success: false,
                newToken: null,
                reason: 'refresh_request_failed',
            };
        }
        if (!refreshResponse) {
            console.warn('Refresh token retornou resposta vazia.');
            return {
                success: false,
                newToken: null,
                reason: 'empty_refresh_response',
            };
        }
        const normalizedMessage = normalizeMessage(refreshResponse);
        const hasErrorFlag = Boolean(refreshResponse.erro ||
            refreshResponse.error ||
            refreshResponse.status === 'erro' ||
            Number(refreshResponse.http_status ?? 0) === 401);
        if (hasErrorFlag && isTemporaryRefreshFailure(refreshResponse, normalizedMessage)) {
            return {
                success: false,
                newToken: null,
                reason: 'refresh_request_failed',
            };
        }
        if (hasErrorFlag) {
            console.warn('Refresh token rejeitado:', refreshResponse);
            return {
                success: false,
                newToken: null,
                reason: 'refresh_rejected',
            };
        }
        const newToken = refreshResponse.access_token ?? refreshResponse.token;
        const newRefreshToken = refreshResponse.refresh_token ?? currentRefreshToken;
        const isSuccess = refreshResponse.sucesso === true ||
            refreshResponse.status === 'sucesso' ||
            (!refreshResponse.status && Boolean(newToken));
        if (!isSuccess || !newToken) {
            return {
                success: false,
                newToken: null,
                reason: 'missing_access_token',
            };
        }
        inMemoryAccessToken = newToken;
        refreshTokenStorage.set(newRefreshToken);
        dispatchAuthTokenUpdate();
        refreshBlockedUntil = 0;
        return {
            success: true,
            newToken,
            reason: 'success',
        };
    };
    refreshInFlightPromise = executeRefresh()
        .then(result => {
        if (!result.success) {
            const shouldApplyCooldown = result.reason === 'refresh_request_failed' ||
                result.reason === 'refresh_rejected' ||
                result.reason === 'missing_access_token' ||
                result.reason === 'empty_refresh_response';
            if (shouldApplyCooldown) {
                refreshBlockedUntil = Date.now() + REFRESH_FAILURE_COOLDOWN_MS;
            }
        }
        return result;
    })
        .finally(() => {
        refreshInFlightPromise = null;
    });
    return refreshInFlightPromise;
};
